/* eslint-disable max-classes-per-file */
import { Observable, of, takeWhile } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { JsonModalComponent, JsonModalResult } from '@src/app/components/modals/json-modal/json-modal.component';
import { RuleEditorModalComponent } from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.component';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { RuleData } from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.utils';
import { catchError, switchMap } from 'rxjs/operators';
import { fixedEnvironment } from '@src/environments/environment.fixed';

/**
 * The `JsonModal` class provides utility functions to open and handle JSON modal dialogs.
 * These dialogs are used to display and process JSON data within the application.
 *
 * See {@link JsonModalDataObject} for the data object used to configure and display a JSON modal dialog.
 * In addition, see {@link JsonModalComponent} for the component used to display the JSON modal dialog.
 *
 * The class also includes a shared handler function `sharedJsonModalHandler` to manage the subscription to dialog results.
 */
export class JsonModal {
  /**
   * Opens a JSON modal dialog with the provided data.
   *
   * @param dialog - The MatDialog instance used to open the dialog.
   * @param data - The data to be passed to the dialog. See {@link JsonModalDataObject}.
   * @returns A reference to the opened dialog.
   */
  static openJSONModalDialog(dialog: MatDialog, data: JsonModalDataObject): MatDialogRef<JsonModalComponent> {
    return dialog.open(
      JsonModalComponent,
      {
        data,
        minWidth: fixedEnvironment.jsonModalWidthLarge,
      }
    );
  }

  /**
   * Subscribes to results from the provided dialog, calling the provided process function with the result.
   * Displays an alert with an error message upon process failure.
   *
   * @param dialogRef - A reference to the opened dialog.
   * @param process - A function to process the JSON result.
   * @param onFailMsg - The message to display on failure.
   * @param onSuccess - A callback function to execute on success.
   * @param onError - A callback function to execute on error (optional).
   */
  static useJsonDialog(
    dialogRef: MatDialogRef<JsonModalComponent>,
    process: (json: JSON) => Observable<boolean>,
    onFailMsg: string,
    onSuccess: () => void,
    onError: (err: Error) => void = (_) => {
    },
  ): void {
    const processJsonFn = (json: JsonModalResult) => process(json.body);
    JsonModal.sharedJsonModalHandler(
      dialogRef, onFailMsg, onSuccess, onError, processJsonFn
    );
  }

  /**
   * Subscribes to results from the dialog, calling the provided process function with the result.
   * Dialog should emit a JSON along with a list of scopes to modify.
   * Displays an alert with an error message upon process failure.
   *
   * @param dialogRef - A reference to the opened dialog.
   * @param process - A function to process the JSON result and scopes.
   * @param onFailMsg - The message to display on failure.
   * @param onSuccess - A callback function to execute on success.
   * @param onError - A callback function to execute on error (optional).
   */
  static useScopedJsonDialog(
    dialogRef: MatDialogRef<JsonModalComponent>,
    process: (json: JSON, scopes: string[]) => Observable<boolean>,
    onFailMsg: string,
    onSuccess: () => void,
    onError: (err: Error) => void = (_) => {
    }
  ): void {
    const processJsonFn = (scopedJson: JsonModalResult) =>
      process(scopedJson.body, scopedJson.scopes?.map(x => x.scope.toString()));
    JsonModal.sharedJsonModalHandler(
      dialogRef, onFailMsg, onSuccess, onError, processJsonFn
    );
  }

  /**
   * Subscribes to results from the rule editor dialog, calling the provided process function with the result.
   * Displays an alert with an error message upon process failure.
   *
   * @param dialogRef - A reference to the opened rule editor dialog.
   * @param process - A function to process the rule data.
   * @param onFailMsg - The message to display on failure.
   * @param onSuccess - A callback function to execute on success.
   * @param onError - A callback function to execute on error (optional).
   */
  static useRuleDialog(
    dialogRef: MatDialogRef<RuleEditorModalComponent>,
    process: (ruleData: RuleData[]) => Observable<boolean>,
    onFailMsg: string,
    onSuccess: () => void,
    onError: (err: Error) => void = (_) => {
    },
  ): void {
    let isOpen = true;
    dialogRef.componentInstance.submit
      .pipe(
        takeWhile(() => isOpen),
        switchMap(ruleEmit => process(ruleEmit).pipe(
          catchError((e) => {
            return of(new JsonModalError(e));
          })
        )),
      ).subscribe((r) => {
        if (r instanceof JsonModalError) {
          onError(r.error);
        } else if (!r) {
          window.alert(onFailMsg);
        } else {
        // on success, close dialog
          onSuccess();
          dialogRef.componentInstance?.closeDialog();
          isOpen = false;
        }
      });
  }

  private static sharedJsonModalHandler(
    dialogRef: MatDialogRef<JsonModalComponent>,
    onFailMsg: string,
    onSuccess: () => void,
    onError: (err: Error) => void = (_) => {
    },
    processJsonFn: (scopedJson: JsonModalResult) => Observable<boolean>
  ): void {
    let isOpen = true;
    dialogRef.componentInstance.submit
      .pipe(
        takeWhile(() => isOpen),
        switchMap(json =>
          processJsonFn(json).pipe(
            catchError((e) => {
              dialogRef.componentInstance.waitingForResponse = false;
              return of(new JsonModalError(e));
            })
          )
        )
      ).subscribe((r) => {
        if (r instanceof JsonModalError) {
          onError(r.error);
        } else if (!r) {
          window.alert(onFailMsg);
        } else {
        // on success, close dialog
          onSuccess();
          isOpen = false;
          dialogRef.componentInstance?.closeDialog();
        }
        dialogRef.componentInstance.waitingForResponse = false;
      });
  }
}

class JsonModalError {
  constructor(public error: Error) {
  }
}

/* eslint-enable max-classes-per-file */
